import { useEffect, useState } from "react";

import Loading from "@/views/loading";

/**
 * 按需加载的路由组件，用法同 React.lazy，额外带一个 `preload()`。
 *
 * 为什么不用 React.lazy + <Suspense>：
 *
 * React 18 的 hydration 要求每个 <Suspense> 边界在 HTML 里都有一对
 * `<!--$-->` / `<!--/$-->` 注释标记 —— 那是 ReactDOMServer 才会输出的东西。
 * react-snap 的产物不是服务端渲染的字符串，而是**真实浏览器里 DOM 的快照**，
 * 注释标记根本不存在（实测预渲染页里 `<!--$-->` 出现 0 次）。
 * 于是客户端 hydrate 到 Suspense 边界时 `canHydrateSuspenseInstance` 必然失败：
 *
 *   Uncaught Error: Hydration failed because the initial UI does not match
 *   what was rendered on the server.
 *       at throwOnHydrationMismatch
 *       at tryToClaimNextHydratableInstance
 *       at updateSuspenseComponent      ← 失败点就是 Suspense 边界本身
 *
 * 紧接着是「整个 root 改走客户端渲染」，预渲染出的那棵完整 DOM 被整棵丢弃重渲染。
 * 注意这跟 lazy chunk 有没有提前下载**无关**：边界自身就不可 hydrate，
 * 所以"预加载 chunk"或"换个 fallback"都解决不了，唯一的出路是让客户端首帧的
 * 组件树里**没有 Suspense**。
 *
 * 这里就回到 Suspense 之前的老办法：自己用 state 管加载。
 * 已 preload 的路由首帧同步拿到真组件，组件树里不出现 Suspense，hydration 对得上；
 * 未 preload 的路由（客户端跳转过去的页面）先渲染 Loading、加载完再换上，
 * 观感与原来的 Suspense fallback 一致。代码分割完全保留。
 */
export function preloadableRoute(factory) {
	let loaded = null;

	// import() 自带去重，重复调用不会重复发请求
	const preload = () =>
		factory().then((mod) => {
			loaded = mod.default;
			return mod.default;
		});

	const Route = (props) => {
		const [Component, setComponent] = useState(() => loaded);

		useEffect(() => {
			if (Component) return;

			let alive = true;
			preload().then(
				(mod) => {
					// 必须包一层函数再传：React 的 setState 遇到函数参数会当「更新函数」
					// 调用（setComponent(Foo) 会执行 Foo(prevState)）。本项目的路由组件
					// 目前都是 memo() 包裹的「对象」，侥幸不触发；但哪天有人挂一个普通
					// 函数组件上来，客户端跳转到它就会炸，且报错形态离真因很远。
					if (alive) setComponent(() => mod);
				},
				(err) => {
					// chunk 拉不到（典型是重新部署后旧 HTML 还指向已删除的 chunk）。
					// 这里把它吞掉并留在 Loading 上，而不是放任一个 unhandled rejection。
					console.error("[router] 路由组件加载失败，请刷新页面重试", err);
				},
			);
			return () => {
				alive = false;
			};
		}, [Component]);

		return Component ? <Component {...props} /> : <Loading />;
	};

	Route.preload = preload;

	return Route;
}

export default preloadableRoute;
